import referralApiService from '@/request/referral/referral.api.service';
import tokenApiService from '@/request/token/token.api.service';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { ITokenOrder } from '@/request/token/token.response.interface';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export interface limitedOrder {
  userPubkey: string;
  createdAt: string;
  expiredAt: string;
  orderNo: string;
  inputMint: string;
  outputMint: string;
  inputSymbol: string;
  outputSymbol: string;
  makingAmount: string;
  takingAmount: string;
  solPrice: string;
  tokenRate: string;
  inputDecimals: number;
  outputDecimals: number;
  transformAmount: boolean;
  status: 'Cancelled' | 'Completed' | 'Active';
}

export const useTokenOrder = () => {
  const { address } = useParams();
  const tokenAddress = address;
  const { walletAddress, isConnected, chain } = useBoomWallet();
  const [marketOrderList, setMarketOrderList] = useState<ITokenOrder[]>([]);
  const [limitedOrderList, setLimitedOrderList] = useState<limitedOrder[]>([]);
  const { subscribe } = useAppEventBusContext();

  const fetchMarketedOrder = useCallback(async () => {
    try {
      if (walletAddress && tokenAddress && isConnected) {
        const res = await tokenApiService.getTokenOrder({ tokenAddress, walletAddress });
        if (res.code == 0) {
          setMarketOrderList(res.data);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [walletAddress, tokenAddress, isConnected]);

  const fetchLimitOrder = useCallback(async () => {
    try {
      if (walletAddress && isConnected) {
        const [history, unsettled] = await Promise.all([
          await referralApiService.getLimitOrderHistoryList({ wallet: walletAddress, page: 1 }),
          await referralApiService.getUnsettledLimitOrderList({ wallet: walletAddress }),
        ]);

        let i = unsettled.length;
        while (i--) {
          if (history.orders.some((item) => item.orderKey.toLowerCase() === unsettled[i].publicKey.toLowerCase())) {
            unsettled.splice(i, 1);
          }
        }

        const list: limitedOrder[] = [];
        if (history?.orders?.length > 0) {
          history.orders.map((item) => {
            if (item.extraData) {
              const order = {
                inputMint: item.inputMint,
                outputMint: item.outputMint,
                makingAmount: item.makingAmount,
                takingAmount: item.takingAmount,
                userPubkey: item.userPubkey,
                createdAt: item.createdAt,
                expiredAt: item.expiredAt,
                transformAmount: false,
                orderNo: '',
                status: item.status as any,
                ...item.extraData,
              };
              list.push(order);
            }
          });
        }
        if (unsettled.length > 0) {
          unsettled.map((item) => {
            const order = {
              inputMint: item.account.inputMint,
              outputMint: item.account.outputMint,
              makingAmount: item.account.makingAmount,
              takingAmount: item.account.takingAmount,
              userPubkey: item.account.maker,
              createdAt: item.account.createdAt,
              transformAmount: true,
              expiredAt: item.account.expiredAt,
              orderNo: item.publicKey,
              decimal: 0,
              status: 'Active' as any,
              ...item.extraData,
            };
            list.push(order);
          });
        }
        const arr = list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setLimitedOrderList(arr);
      }
    } catch (error) {
      console.log(error);
    }
  }, [walletAddress, isConnected]);

  const fetchData = () => {
    fetchMarketedOrder();
    if (chain == 'solana') {
      fetchLimitOrder();
    }
  };

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Balance, () => {
      setTimeout(() => {
        fetchData();
      }, 5000);
    });
    return () => unsubscribe();
  }, [fetchData, subscribe]);

  useEffect(() => {
    fetchData();
  }, [walletAddress, tokenAddress, isConnected, chain]);

  return {
    fetchData,
    limitedOrderList,
    marketOrderList,
  };
};
